import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { Pagination } from 'components/molecules/Pagination';
import { ERouteLinks } from 'models/route';
import { Table } from 'components/molecules/Table';
import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useNavigate, useParams } from 'react-router-dom';
import { CardList } from 'components/molecules/CardList';
import { Loader } from 'components/atoms/Loader';
import { EPerPages, ISortOptions } from 'models/table';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { IComponentsTransactionsFormValues } from 'models/ancillaries';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Box } from '@mui/system';
import { Alert, EAlertVariants, IAlert } from 'components/atoms/Alert';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { EAncillaryState } from 'constants/ArtAccessoryType';
import { ELegalVisibility } from 'constants/LegalVisibility';
import { useAuth } from 'models/authContext';
import { useAccount } from 'pages/AccountPage/hooks';
import { useLang } from 'models/langContext';
import { ComponentsTransactionsSearchForm } from '../ComponentsTransactionsSearchForm';
import {
  ComponentsTranslationsTableColumns,
  mapComponentsTranslationsToDataSource,
} from '../helpers';
import { ComponentsTransactionsCard } from '../ComponentsTransactionsCard';

interface IComponentsTransactions {
  inGroup: boolean;
  stateUniqueId: string | undefined;
  ownerVisibilityUniqueId: string | undefined;
  ownerId: string | undefined;
  keeperVisibilityUniqueId: string | undefined;
  keeperId: string | undefined;
}

const ComponentsTransactions = ({
  inGroup,
  stateUniqueId,
  keeperVisibilityUniqueId,
  ownerVisibilityUniqueId,
  keeperId,
  ownerId,
}: IComponentsTransactions) => {
  const { isDesktop } = useThemeBreakpoint();
  const navigate = useNavigate();
  const { clearAlert, setAlert, samePageAlert } = useAlert();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslations();
  const { midDateFormat } = useGlobalProperty();
  const { hasPermissions } = usePermission();
  const {
    getComponentsTransactionsData,
    paginatorComponentsTransactions,
    componentsTransactions,
    discardableTransactionId,
    getDiscardableTransactionId,
  } = useAncillaries();

  const [currentPageComponentsTransactions, setCurrentPageComponentsTransactions] =
    useState<number>(1);
  const [perPageComponentsTransactions, setPerPageComponentsTransactions] =
    useState<EPerPages>(EPerPages.perPage25);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState<boolean | null>(null);
  const [transactionsQuery, setTransactionsQuery] = useState<
    Partial<IComponentsTransactionsFormValues>
  >({});
  const [transactionSort, setTransactionSort] = useState<ISortOptions | null>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);

  const { user, getUser } = useAuth();
  const { selectedLanguage } = useLang();
  const { account, getSelfAccountData } = useAccount();

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    (async function getAccountDataInit() {
      if (user?.userId) {
        await getSelfAccountData();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    (async function getDataInit() {
      if (id) {
        if (!componentsTransactions.length) {
          setIsTransactionsLoading(true);
        }
        setIsPaginationLoading(true);

        await getComponentsTransactionsData(
          id,
          currentPageComponentsTransactions,
          perPageComponentsTransactions,
          {
            ...transactionsQuery,
          },
          transactionSort,
        );
        getDiscardableTransactionId(id);
        setIsTransactionsLoading(false);
        setIsPaginationLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    currentPageComponentsTransactions,
    perPageComponentsTransactions,
    transactionsQuery,
    transactionSort,
    selectedLanguage,
  ]);

  useEffect(
    () => () => {
      setIsTransactionsLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleAddTransaction = () => {
    if (inGroup) {
      clearAlert(ActionTypes.SET_SAME_PAGE_ALERT);
      clearAlert(ActionTypes.SET_TRANSACTION_ALERT);
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('ancillary.transaction.addTransaction.text'),
        variant: EAlertVariants.warning,
      } as IAlert);
    } else {
      navigate(ERouteLinks.AncillariesMiddleTransaction.replace(':id', id as string));
    }
  };

  const isAddTransactionVisible = () => {
    if (
      [EAncillaryState.DISABLED, EAncillaryState.DESTROYED].includes(
        stateUniqueId as EAncillaryState,
      )
    )
      return false;
    if (hasPermissions(EPermission.VIEW_ALL_LE)) return true;
    if (hasPermissions(EPermission.VIEW_LE)) {
      return (
        (keeperVisibilityUniqueId !== ELegalVisibility.NON_PUBLIC ||
          account?.assignedLegalIds.includes(keeperId!)) &&
        (ownerVisibilityUniqueId !== ELegalVisibility.NON_PUBLIC ||
          account?.assignedLegalIds.includes(ownerId!))
      );
    }
    return false;
  };

  return (
    <div>
      <ComponentsTransactionsSearchForm onSubmit={setTransactionsQuery} />
      <Grid
        item
        xs={12}
        container
        sx={{ display: 'flex', justifyContent: 'flex-end', margin: '24px 0' }}
      >
        {isAddTransactionVisible() && (
          <Button
            variant={EButtonVariants.contained}
            icon={EIconTypes.plus}
            label={t('componentDetails.transaction.addTransaction.button')}
            onClick={handleAddTransaction}
            permission={[EPermission.ADD_TRANSACTION]}
          />
        )}
      </Grid>
      {samePageAlert && (
        <Box mt={2} sx={{ marginBottom: '10px' }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
      {isTransactionsLoading ? (
        <Loader isLoading={isTransactionsLoading} />
      ) : (
        <>
          {isDesktop ? (
            <Table
              columns={ComponentsTranslationsTableColumns({
                handleDelete:
                  hasPermissions([
                    EPermission.VIEW_ALL_TRANSACTIONS,
                    EPermission.DISCARD_TRANSACTION,
                  ]) ||
                  hasPermissions([
                    EPermission.VIEW_TRANSACTIONS,
                    EPermission.DISCARD_TRANSACTION,
                  ])
                    ? (transactionId: string) =>
                        navigate(ERouteLinks.CancelTransaction.replace(':id', transactionId))
                    : null,
                discardableTransactionId,
                dateFormat: midDateFormat,
              })}
              dataSource={mapComponentsTranslationsToDataSource(componentsTransactions)}
              onSort={setTransactionSort}
              translationsKeys={{
                noResults: 'ancillaries.accordion.noResults.text',
              }}
            />
          ) : (
            <CardList
              items={mapComponentsTranslationsToDataSource(componentsTransactions)}
              render={(data, index) => (
                <ComponentsTransactionsCard
                  data={data}
                  key={index}
                  discardableTransactionId={discardableTransactionId}
                  handleView={(idTransactions: string) =>
                    navigate(ERouteLinks.ViewTransaction.replace(':id', idTransactions))
                  }
                />
              )}
            />
          )}

          {paginatorComponentsTransactions && (
            <Pagination
              count={paginatorComponentsTransactions?.totalElements}
              perPage={perPageComponentsTransactions}
              onChangePage={(page) => {
                setCurrentPageComponentsTransactions(page);
              }}
              onChangePerPage={(value) => {
                setPerPageComponentsTransactions(value);
              }}
              current={currentPageComponentsTransactions}
              isLoading={isPaginationLoading}
            />
          )}
        </>
      )}
    </div>
  );
};

export { ComponentsTransactions };
